import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import TagChip from '../../atoms/TagChip/TagChip';

/**
 * TagSummary - Exibe tags compactadas em um limite fixo.
 * Mantem a API anterior, mas evita medir layout e renderizar tags duplicadas em cada card.
 */
const TagSummary = ({ tags, maxWidth = '90%', fontSize = undefined, chipSx = {}, onTagRemove, onTagClick, children, compact = false, maxVisible = 3, maxRows = 1 }) => {
  // Heurística de line-packing para evitar que tags quebrem para uma 3ª linha
  // e sejam escondidas pelo CSS sem irem para o contador de hiddenTags.
  const MAX_CHARS_PER_LINE = 38; // Capacidade ajustada
  const BADGE_COST = 5; // Espaço reservado apenas para o botão +X (AddTagButton foi movido)
  let currentLineChars = 0;
  let currentLines = 1;
  let dynamicMaxVisible = 0;

  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i];
    const label = typeof tag === 'object' ? (tag.name || tag.label || tag._id) : tag;
    // Tamanho da string + peso reduzido para paddings/ícone
    const tagCost = (label ? label.length : 0) + 4;
    
    // Verifica se a tag sozinha vai quebrar a linha
    let willBreakLine = currentLineChars > 0 && (currentLineChars + tagCost > MAX_CHARS_PER_LINE);
    let newLineChars = willBreakLine ? tagCost : (currentLineChars > 0 ? currentLineChars + tagCost + 1 : tagCost);
    let newLines = willBreakLine ? currentLines + 1 : currentLines;

    // Se adicionar a tag já estoura as linhas permitidas, paramos.
    if (newLines > maxRows) {
      break;
    }
    
    // Verifica se, APÓS adicionar a tag, ainda sobrará espaço para os botões finais (+X ou Add)
    // na linha atual ou na próxima. Se os botões forem empurrados para além do maxRows,
    // não podemos incluir a tag atual.
    let willBadgeBreakLine = newLineChars + BADGE_COST > MAX_CHARS_PER_LINE;
    let badgeLines = willBadgeBreakLine ? newLines + 1 : newLines;
    
    if (badgeLines > maxRows) {
      break;
    }
    
    currentLineChars = newLineChars;
    currentLines = newLines;
    dynamicMaxVisible++;
  }

  // Usa o menor valor entre o limite configurado (prop) e o limite dinâmico calculado
  let actualMaxVisible = Math.min(maxVisible, dynamicMaxVisible);
  // Garante ao menos 1 tag se houverem tags
  if (actualMaxVisible === 0 && tags.length > 0) actualMaxVisible = 1;

  const visibleTags = tags.slice(0, actualMaxVisible);
  const hiddenTags = tags.slice(actualMaxVisible);

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', width: compact ? 'auto' : '100%', maxWidth, minWidth: 0 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        alignContent: 'flex-start',
        flexWrap: maxRows > 1 ? 'wrap' : 'nowrap',
        gap: 0.5,
        width: '100%', // Usa 100% do espaço disponível em vez de travar em 90%
        minWidth: 0,
        overflow: 'hidden',
        maxHeight: maxRows > 1 ? `calc(${maxRows} * 24px)` : undefined
      }}>
        {visibleTags.map((tag) => (
          <span key={typeof tag === 'object' ? tag._id : tag} style={{ display: 'inline-block' }}>
            <TagChip
              label={typeof tag === 'object' ? (tag.name || tag.label || tag._id) : tag}
              fontSize={fontSize}
              size="small"
              sx={chipSx}
              onDelete={onTagRemove ? () => onTagRemove(typeof tag === 'object' ? tag._id : tag) : undefined}
              onClick={onTagClick ? () => onTagClick(tag) : undefined}
            />
          </span>
        ))}
        {/* Renderiza o botão +X dentro do mesmo container flex, permitindo fluxo natural */}
        <Box sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          marginLeft: 0.5,
          flexShrink: 0
        }}>
          {children && children({ visibleTags, hiddenTags })}
        </Box>
      </Box>
    </Box>
  );
};

TagSummary.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string,
      label: PropTypes.string
    })
  ])).isRequired,
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fontSize: PropTypes.string,
  chipSx: PropTypes.object,
  onTagRemove: PropTypes.func,
  onTagClick: PropTypes.func,
  children: PropTypes.func,
  compact: PropTypes.bool,
  maxVisible: PropTypes.number,
  maxRows: PropTypes.number
};

export default React.memo(TagSummary);
